import { computed, type MaybeRefOrGetter, ref, toValue, watch } from "vue"
import type { ResultOf } from "@graphql-typed-document-node/core"
import { fetchRecordSources } from "@auger/core/gql"
import type { RecordFieldsFragment } from "@auger/core/gql/types"
import { getAuger } from "@/init/auger"
import { getType } from "@/stores/types"
import { errorMessage } from "@/utils/errors"
import type { ComposableDeps } from "./types"

export type SourceRow = ResultOf<typeof fetchRecordSources>["sources"][number]
export type OptimisticSource = Omit<SourceRow, "id" | "enrichment_pending"> & {
  id?: number
  enrichment_pending?: SourceRow["enrichment_pending"]
  __optimistic?: boolean
}

/**
 * A record's sources, its links to external systems (Pipedrive, Airtable,
 * HubSpot, Mailchimp, Luma, Sanity, Slack): fetched whenever the record's id
 * changes (fetch() refetches them), and added and removed optimistically.
 */
export function useSources(
  recordRef: MaybeRefOrGetter<Pick<RecordFieldsFragment, "id" | "type"> | null | undefined>,
  deps: ComposableDeps
) {
  const { client, toast } = deps

  // --- State ---
  const sources = ref<OptimisticSource[]>([])
  const loading = ref(false)
  const saving = ref(false)
  const error = ref<unknown>(null)

  // --- Computed ---

  const recordId = computed(() => toValue(recordRef)?.id)
  const recordType = computed(() => toValue(recordRef)?.type)
  const count = computed(() => sources.value.length)
  const hasItems = computed(() => count.value > 0)

  // --- Helpers ---

  /** Keeps search results the record isn't already linked to. */
  function sourceFilter(source: { sourceType: string; sourceId: string | number }) {
    const hasSource = (s: OptimisticSource) =>
      s.source_type === source.sourceType && s.source_id === `${source.sourceId}`
    return !sources.value.some(hasSource)
  }

  // --- Local State Helpers ---

  function findSource(sourceType: string, sourceId: string | number) {
    return sources.value.find(
      (s) => s.source_type === sourceType && s.source_id === String(sourceId)
    )
  }

  function removeSourceLocally(sourceType: string, sourceId: string | number) {
    sources.value = sources.value.filter(
      (s) => !(s.source_type === sourceType && s.source_id === String(sourceId))
    )
  }

  // --- Data Fetching ---

  function fetch(): Promise<OptimisticSource[]> {
    if (!recordId.value) return Promise.resolve([])

    loading.value = true
    error.value = null

    return client
      .query({
        query: fetchRecordSources,
        variables: { recordId: recordId.value },
        fetchPolicy: "network-only",
      })
      .then(({ data }) => {
        sources.value = [...(data?.sources ?? [])]
        return sources.value
      })
      .catch((err) => {
        const message = errorMessage(err)
        error.value = err
        toast?.error?.(`Failed to load sources: ${message}`)
        throw err
      })
      .finally(() => {
        loading.value = false
      })
  }

  // --- Mutations ---

  /** Links a source, showing it optimistically until the write lands. */
  function add({
    sourceType,
    sourceId,
    context,
  }: {
    sourceType: string
    sourceId: string | number
    context?: unknown
  }) {
    if (!recordId.value) return Promise.reject(new Error("No record"))
    if (!recordType.value) {
      toast?.error?.("Cannot link source: record type not yet loaded")
      return Promise.reject(new Error("No record type"))
    }

    if (!sourceType.includes("_")) {
      toast?.error?.(`Cannot link source: invalid source type format "${sourceType}"`)
      return Promise.reject(new Error(`Invalid source type format: ${sourceType}`))
    }

    const validSourceTypes = getType(recordType.value)?.sourceTypes ?? []
    if (!validSourceTypes.includes(sourceType)) {
      toast?.error?.(`Cannot link source: "${sourceType}" is not valid for ${recordType.value}`)
      return Promise.reject(new Error(`Unknown source type: ${sourceType}`))
    }

    saving.value = true

    const optimistic: OptimisticSource = {
      source_type: sourceType,
      source_id: String(sourceId),
      context: (context ?? null) as OptimisticSource["context"],
      created_at: new Date().toISOString(),
      __optimistic: true,
    }
    sources.value = [...sources.value, optimistic]

    const id = recordId.value
    return getAuger(client)
      .sources.add({
        recordId: id,
        sourceType,
        sourceId: String(sourceId),
        context: (context ?? undefined) as Record<string, unknown> | undefined,
      })
      .then((created) => {
        sources.value = sources.value.map((s) =>
          s.__optimistic && s.source_type === sourceType && s.source_id === String(sourceId)
            ? created
            : s
        )
        toast?.success?.(`${sourceType} source linked`)
        return created
      })
      .catch((err) => {
        const error = errorMessage(err)
        sources.value = sources.value.filter(
          (s) =>
            !(s.__optimistic && s.source_type === sourceType && s.source_id === String(sourceId))
        )
        toast?.error?.(`Failed to link source: ${error}`)
        throw err
      })
      .finally(() => {
        saving.value = false
      })
  }

  /** Unlinks a source, putting it back in place if the write fails. */
  function remove({
    sourceType,
    sourceId,
  }: {
    sourceType: string
    sourceId?: string | number
  }): Promise<void> {
    if (!recordId.value) {
      toast?.error?.("Cannot unlink source: no record")
      return Promise.reject(new Error("No record"))
    }
    if (!sourceId) {
      toast?.error?.("Cannot unlink source: missing source id")
      return Promise.reject(new Error("No source id"))
    }

    const removed = findSource(sourceType, sourceId)
    const index = removed ? sources.value.indexOf(removed) : -1

    saving.value = true

    removeSourceLocally(sourceType, sourceId)

    const id = recordId.value
    return getAuger(client)
      .sources.remove(id, sourceType, String(sourceId))
      .then(() => {
        toast?.success?.(`${sourceType} source unlinked`)
      })
      .catch((err) => {
        const error = errorMessage(err)
        if (removed && index !== -1) {
          const newSources = [...sources.value]
          newSources.splice(index, 0, removed)
          sources.value = newSources
        }
        toast?.error?.(`Failed to unlink source: ${error}`)
        throw err
      })
      .finally(() => {
        saving.value = false
      })
  }

  /** Whether the source is linked; without a sourceId, whether any of that type is. */
  function hasSource(sourceType: string, sourceId?: string | number): boolean {
    // Truthy check preserves original semantics: 0/"" treated as "any of type"
    if (sourceId) {
      return sources.value.some(
        (s) => s.source_type === sourceType && s.source_id === String(sourceId)
      )
    }
    return sources.value.some((s) => s.source_type === sourceType)
  }

  function getByType(sourceType: string): OptimisticSource[] {
    return sources.value.filter((s) => s.source_type === sourceType)
  }

  // --- Watch for record changes ---
  watch(
    recordId,
    (newId, oldId) => {
      if (newId && newId !== oldId) {
        // Errors are already surfaced via toast + error.value inside fetch();
        // swallow here to avoid an unhandled rejection from the watcher.
        fetch().catch(() => {})
      }
    },
    { immediate: true }
  )

  return {
    // Data
    sources,
    loading,
    saving,
    error,

    // Computed
    count,
    hasItems,

    // Methods
    fetch,
    add,
    remove,
    hasSource,
    getByType,
    sourceFilter,
  }
}
